import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  BadgeCheck,
} from "lucide-react";
import { useLocation } from "react-router-dom";
const getInitials = (name) => {
  if (!name) return "";
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
};

const ResponsiveProfileImage = ({ imageUrls, alt, className }) => {
  if (!imageUrls) return null;

  const small = imageUrls.small || imageUrls.medium || imageUrls.large;
  const medium = imageUrls.medium || imageUrls.large || small;
  const large = imageUrls.large || medium || small;

  return (
    <picture>
      <source media="(max-width: 768px)" srcSet={`${small} 1x, ${medium} 2x`} />
      <source media="(min-width: 769px)" srcSet={`${medium} 1x, ${large} 2x`} />
      <img
        src={medium}
        alt={alt}
        className={className}
        loading="lazy"
        decoding="async"
      />
    </picture>
  );
};


const InfoRow = ({ label, value }) => (
  <div className="py-4 border-b border-gray-200 last:border-b-0">
    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
      {label}
    </p>
    {typeof value === "object" ? (
      value
    ) : (
      <p className={`text-sm ${value ? "text-gray-900 font-medium" : "text-gray-400 italic"}`}>
        {value || "No details added yet."}
      </p>
    )}
  </div>
);

const ProfileDetailedView = ({ userData, onBack }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleEditClick = () => {
    navigate("/edit-profile", {
      state: { from: location.pathname },
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header Navbar */}
      <div className="sticky top-0 bg-white z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-1">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-black" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">My Profile</h1>
          </div>
          <button
            onClick={handleEditClick}
            className="border border-black text-gray-900 px-4 py-1.5 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
          >
            Edit
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Profile Picture Section - Centered */}
        <div className="flex justify-center mb-8">
          <div className="relative w-32 h-32">
            {userData.profilePicture ? (
              <ResponsiveProfileImage
                imageUrls={userData.profilePicture}
                alt={userData.name}
                className="w-full h-full rounded-full object-cover shadow-md"
              />
            ) : (
              <div className="w-full h-full rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center border-2 border-slate-300">
                {userData.name ? (
                  <span className="text-slate-600 font-bold text-3xl">
                    {getInitials(userData.name)}
                  </span>
                ) : (
                  <svg
                    className="w-full h-full text-slate-400"
                    viewBox="0 0 100 100"
                    fill="currentColor"
                  >
                    <circle cx="50" cy="35" r="18" />
                    <path d="M50 58 C 25 58, 10 65, 10 100 L 90 100 C 90 65, 75 58, 50 58 Z" />
                  </svg>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Information in Single Column */}
        <div className="space-y-0">
          <div className="py-4 border-b border-gray-200">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Name
            </p>
            <p className="text-sm text-gray-900 font-medium">
              {userData.name || "No details added yet."}
            </p>
          </div>

          {/* Username with verified badge */}
          <div className="py-4 border-b border-gray-200">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Username
            </p>
            <div className="flex items-center gap-1">
              <p className="text-sm text-gray-900 font-medium">
                {userData.username ? `${userData.username}` : "No details added yet."}
              </p>
              {userData.isVerified && userData.username && (
                <BadgeCheck className="w-4 h-4 fill-[#b0d284] stroke-white flex-shrink-0" />
              )}
            </div>
          </div>

          {/* Email */}
          <InfoRow label="Email" value={userData.email} />

          {/* Gender */}
          <InfoRow label="Gender" value={userData.gender} />

          {/* Birthday */}
          <InfoRow
            label="Birthday"
            value={
              userData.birthday
                ? new Date(userData.birthday).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : null
            }
          />

          {/* Location */}
          <InfoRow label="Location" value={userData.location} />

          {/* Website */}
          <InfoRow
            label="Website"
            value={
              userData.website ? (
                <a
                  href={
                    userData.website.startsWith("http")
                      ? userData.website
                      : `https://${userData.website}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm break-all"
                >
                  {userData.website}
                </a>
              ) : null
            }
          />

          {/* Bio */}
          <InfoRow label="Bio" value={userData.bio} />

          {/* Work Experience - Company */}
          <InfoRow
            label="Company"
            value={userData.workExperience?.currentCompany}
          />

          {/* Work Experience - Position */}
          <InfoRow
            label="Position"
            value={userData.workExperience?.position}
          />

          {/* Work Experience - Years */}
          <InfoRow
            label="Years of Experience"
            value={userData.workExperience?.yearsOfExperience}
          />

          {/* Social - Twitter */}
          <InfoRow
            label="X (Twitter)"
            value={userData.socialProfiles?.twitter}
          />

          {/* Social - Instagram */}
          <InfoRow
            label="Instagram"
            value={userData.socialProfiles?.instagram}
          />
        </div>
      </div>
    </div>
  );
};

export default ProfileDetailedView;